import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Button,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  useTheme,
} from "@mui/material";
import ContactsIcon from "@mui/icons-material/Contacts";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
// MessageIcon removed
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import HomeIcon from "@mui/icons-material/Home";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HistoryIcon from "@mui/icons-material/History";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { format } from "date-fns";

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contact-tabpanel-${index}`}
      aria-labelledby={`contact-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ContactInfo = ({
  athlete,
  contacts = [],
  communications = [],
  onContactUpdate,
  onCommunicationUpdate,
}) => {
  const theme = useTheme();

  const [tabValue, setTabValue] = useState(0);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [openCommunicationDialog, setOpenCommunicationDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [contactMenuAnchorEl, setContactMenuAnchorEl] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactFormData, setContactFormData] = useState({
    id: null,
    relationship: "parent", // Changed from 'type' to 'relationship' to match backend
    name: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [communicationFormData, setCommunicationFormData] = useState({
    id: null,
    contactId: "",
    type: "phone",
    date: new Date().toISOString(),
    notes: "",
    outcome: "completed",
    followUpNeeded: false,
    followUpDate: null,
  });

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle contact menu open
  const handleContactMenuOpen = (event, contact) => {
    setSelectedContact(contact);
    setContactMenuAnchorEl(event.currentTarget);
  };

  // Handle contact menu close
  const handleContactMenuClose = () => {
    setContactMenuAnchorEl(null);
  };

  // Handle add contact
  const handleAddContact = () => {
    setEditMode(false);
    setContactFormData({
      id: null,
      relationship: "parent", // Changed from 'type' to 'relationship' to match backend
      name: "",
      phone: "",
      email: "",
      notes: "",
    });
    setOpenContactDialog(true);
  };

  // Handle edit contact
  const handleEditContact = () => {
    setEditMode(true);
    setContactFormData({
      ...selectedContact,
      id: selectedContact.id || selectedContact._id || selectedContact.name, // Ensure ID is set
    });
    setOpenContactDialog(true);
    handleContactMenuClose();
  };

  // Handle delete contact
  const handleDeleteContact = () => {
    if (window.confirm("Are you sure you want to delete this contact?")) {
      onContactUpdate({
        ...selectedContact,
        deleted: true,
      });
      handleContactMenuClose();
    }
  };

  // Handle contact form field change
  const handleContactFormChange = (field, value) => {
    setContactFormData({
      ...contactFormData,
      [field]: value,
    });
  };

  // Handle contact form submit
  const handleContactSubmit = () => {
    const contactData = {
      ...contactFormData,
      // For new contacts, don't send ID - let MongoDB generate proper ObjectId
      // For existing contacts, keep the existing ID
      id: editMode ? contactFormData.id : undefined,
    };

    onContactUpdate(contactData);
    setOpenContactDialog(false);
  };

  // Handle add communication
  const handleAddCommunication = (contactId = "") => {
    setCommunicationFormData({
      id: null,
      contactId: contactId,
      type: "phone",
      date: new Date().toISOString(),
      notes: "",
      outcome: "completed",
      followUpNeeded: false,
      followUpDate: null,
    });
    setOpenCommunicationDialog(true);
  };

  // Handle communication form field change
  const handleCommunicationFormChange = (field, value) => {
    setCommunicationFormData({
      ...communicationFormData,
      [field]: value,
    });
  };

  // Handle communication form submit
  const handleCommunicationSubmit = () => {
    const communicationData = {
      ...communicationFormData,
      // Don't send numeric ID to backend - let MongoDB generate proper ObjectId
      id: undefined,
      // Ensure contactId is properly formatted
      contactId: communicationFormData.contactId || "",
    };

    onCommunicationUpdate(communicationData);
    setOpenCommunicationDialog(false);
  };

  // Copy to clipboard
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    // Could add a snackbar notification here
  };

  // Get contact type icon
  const getContactTypeIcon = (relationship) => {
    switch (relationship) {
      case "parent":
      case "guardian":
        return <PersonIcon />;
      case "coach":
        return <SchoolIcon />;
      case "counselor":
        return <PersonIcon />;
      default:
        return <ContactsIcon />;
    }
  };

  // Get communication type icon
  const getCommunicationTypeIcon = (type) => {
    switch (type) {
      case "phone":
        return <PhoneIcon />;
      case "email":
        return <EmailIcon />;
      case "text":
        return <EmailIcon />;
      case "in-person":
        return <EventIcon />;
      default:
        return <EmailIcon />;
    }
  };

  // Get contact by ID
  const getContactById = (id) => {
    if (!id) return { name: "Unknown Contact" };

    return (
      contacts.find(
        (contact) =>
          contact.id === id || contact._id === id || contact.name === id
      ) || {
        name: "Unknown Contact",
      }
    );
  };

  // Sort contacts by relationship
  const sortedContacts = [...(contacts || [])].sort((a, b) => {
    // Parents first, then guardians, then coaches, then counselors, then others
    const relationshipOrder = {
      parent: 0,
      guardian: 1,
      coach: 2,
      counselor: 3,
    };
    return (
      (relationshipOrder[a.relationship] || 4) -
      (relationshipOrder[b.relationship] || 4)
    );
  });

  // Sort communications by date (newest first)
  const sortedCommunications = [...(communications || [])].sort((a, b) => {
    return new Date(b.date) - new Date(a.date);
  });

  // Helper function to get contact identifier
  const getContactIdentifier = (contact) => {
    return (
      contact.id || contact._id || contact.name || `contact-${Math.random()}`
    );
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "stretch",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
        >
          <ContactsIcon sx={{ mr: 1 }} color="primary" />
          Contact Information
        </Typography>

        {/* Buttons Row */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "flex-start" },
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddContact}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1,
              flex: { xs: 1, sm: "none" },
            }}
          >
            Add Contact
          </Button>

          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={() => handleAddCommunication()}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1,
              flex: { xs: 1, sm: "none" },
            }}
          >
            Log Communication
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="contact tabs"
        >
          <Tab label="Contacts" icon={<ContactsIcon />} iconPosition="start" />
          <Tab
            label="Communication History"
            icon={<HistoryIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Contacts Tab */}
      <TabPanel value={tabValue} index={0}>
        {sortedContacts.length > 0 ? (
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                maxHeight: "70vh", // Use viewport height instead of fixed pixels
                overflowY: "auto", // Enable vertical scrolling
                pr: 1, // Add right padding for scrollbar
                "&::-webkit-scrollbar": {
                  width: 8,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.3)",
                  borderRadius: 4,
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.5)",
                  },
                },
              }}
            >
              {sortedContacts.map((contact) => (
                <Card
                  key={
                    contact.id ||
                    contact._id ||
                    `contact-${contact.name}-${contact.phone}`
                  }
                  variant="outlined"
                  sx={{
                    width: "100%",
                    minHeight: "fit-content", // Ensure cards maintain their natural height
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    border: `1px solid ${theme.palette.divider}`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: theme.shadows[4],
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardHeader
                    avatar={
                      <Avatar
                        sx={{
                          bgcolor:
                            contact.relationship === "parent"
                              ? theme.palette.primary.main
                              : contact.relationship === "guardian"
                              ? theme.palette.secondary.main
                              : contact.relationship === "coach"
                              ? theme.palette.info.main
                              : theme.palette.warning.main,
                          width: 48,
                          height: 48,
                          fontSize: "1.2rem",
                          fontWeight: 600,
                        }}
                      >
                        {contact.name.charAt(0)}
                      </Avatar>
                    }
                    title={
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {contact.name}
                      </Typography>
                    }
                    subheader={
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                      >
                        <Chip
                          label={
                            contact.relationship.charAt(0).toUpperCase() +
                            contact.relationship.slice(1)
                          }
                          size="small"
                          sx={{
                            mr: 0.5,
                            fontWeight: 500,
                            backgroundColor: theme.palette.primary.light,
                            color: theme.palette.primary.contrastText,
                          }}
                        />
                      </Box>
                    }
                    action={
                      <IconButton
                        aria-label="settings"
                        onClick={(e) => handleContactMenuOpen(e, contact)}
                        sx={{
                          color: theme.palette.text.secondary,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    }
                  />
                  <CardContent sx={{ pt: 0 }}>
                    <List dense disablePadding>
                      {contact.phone && (
                        <ListItem disablePadding sx={{ mb: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <PhoneIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={contact.phone}
                            primaryTypographyProps={{
                              variant: "body2",
                              sx: { fontWeight: 500 },
                            }}
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Copy phone number">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleCopy(contact.phone)}
                                sx={{
                                  color: theme.palette.text.secondary,
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.primary.light,
                                    color: theme.palette.primary.main,
                                  },
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      )}
                      {contact.email && (
                        <ListItem disablePadding sx={{ mb: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <EmailIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={contact.email}
                            primaryTypographyProps={{
                              variant: "body2",
                              sx: { fontWeight: 500 },
                            }}
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Copy email">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleCopy(contact.email)}
                                sx={{
                                  color: theme.palette.text.secondary,
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.primary.light,
                                    color: theme.palette.primary.main,
                                  },
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      )}
                    </List>

                    {contact.notes && (
                      <Box
                        sx={{
                          mt: 2,
                          mb: 2,
                          p: 1.5,
                          backgroundColor: theme.palette.background.default,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.6 }}
                        >
                          <strong>Notes:</strong> {contact.notes}
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        pt: 1,
                      }}
                    >
                      <Button
                        size="small"
                        startIcon={<EmailIcon />}
                        onClick={() => handleAddCommunication(contact.id)}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 500,
                          px: 2,
                        }}
                      >
                        Log Contact
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <ContactsIcon
              sx={{ fontSize: 60, color: "text.disabled", mb: 2 }}
            />
            <Typography variant="h6" gutterBottom>
              No Contacts Added
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add contacts to keep track of the athlete, parents, and coaches.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddContact}
            >
              Add First Contact
            </Button>
          </Box>
        )}
      </TabPanel>

      {/* Communication History Tab */}
      <TabPanel value={tabValue} index={1}>
        {sortedCommunications.length > 0 ? (
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxHeight: 400,
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: 8,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.3)",
                  borderRadius: 4,
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.5)",
                  },
                },
              }}
            >
              {sortedCommunications.map((comm) => (
                <Card
                  key={comm.id}
                  variant="outlined"
                  sx={{
                    width: "100%",
                    minHeight: "fit-content",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    border: `1px solid ${theme.palette.divider}`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: theme.shadows[4],
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardHeader
                    avatar={
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          width: 48,
                          height: 48,
                          fontSize: "1.2rem",
                          fontWeight: 600,
                        }}
                      >
                        {getCommunicationTypeIcon(comm.type)}
                      </Avatar>
                    }
                    title={
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {comm.type.charAt(0).toUpperCase() + comm.type.slice(1)}{" "}
                        with {getContactById(comm.contactId).name} (
                        {getContactById(comm.contactId).relationship})
                      </Typography>
                    }
                    subheader={
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                      >
                        <Chip
                          label={comm.outcome}
                          size="small"
                          sx={{
                            mr: 0.5,
                            fontWeight: 500,
                            backgroundColor:
                              comm.outcome === "completed"
                                ? theme.palette.success.main
                                : comm.outcome === "left-message"
                                ? theme.palette.warning.main
                                : comm.outcome === "no-answer"
                                ? theme.palette.error.main
                                : theme.palette.info.main,
                            color:
                              comm.outcome === "completed"
                                ? theme.palette.success.contrastText
                                : comm.outcome === "left-message"
                                ? theme.palette.warning.contrastText
                                : comm.outcome === "no-answer"
                                ? theme.palette.error.contrastText
                                : theme.palette.info.contrastText,
                          }}
                        />
                        {comm.followUpNeeded && (
                          <Chip
                            label={`Follow up: ${format(
                              new Date(comm.followUpDate),
                              "MMM d"
                            )}`}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ ml: 0.5 }}
                          />
                        )}
                      </Box>
                    }
                  />
                  <CardContent sx={{ pt: 0 }}>
                    <List dense disablePadding>
                      <ListItem disablePadding sx={{ mb: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <EventIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={format(
                            new Date(comm.date),
                            "MMM d, yyyy h:mm a"
                          )}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: { fontWeight: 500 },
                          }}
                        />
                      </ListItem>
                    </List>

                    {comm.notes && (
                      <Box
                        sx={{
                          mt: 2,
                          mb: 2,
                          p: 1.5,
                          backgroundColor: theme.palette.background.default,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.6 }}
                        >
                          <strong>Notes:</strong> {comm.notes}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <HistoryIcon sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Communication History
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Log your communications with the athlete and their contacts.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleAddCommunication()}
            >
              Log First Communication
            </Button>
          </Box>
        )}
      </TabPanel>

      {/* Contact Menu */}
      <Menu
        anchorEl={contactMenuAnchorEl}
        open={Boolean(contactMenuAnchorEl)}
        onClose={handleContactMenuClose}
      >
        <MenuItem onClick={handleEditContact}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Contact" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleAddCommunication(selectedContact?.id);
            handleContactMenuClose();
          }}
        >
          <ListItemIcon>
            <EmailIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Log Communication" />
        </MenuItem>
        <MenuItem onClick={handleDeleteContact}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete Contact" />
        </MenuItem>
      </Menu>

      {/* Add/Edit Contact Dialog */}
      <Dialog
        open={openContactDialog}
        onClose={() => setOpenContactDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editMode ? "Edit Contact" : "Add New Contact"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={contactFormData.name}
                onChange={(e) =>
                  handleContactFormChange("name", e.target.value)
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Contact Type"
                value={contactFormData.relationship}
                onChange={(e) =>
                  handleContactFormChange("relationship", e.target.value)
                }
              >
                <MenuItem value="parent">Parent</MenuItem>
                <MenuItem value="guardian">Guardian</MenuItem>
                <MenuItem value="coach">Coach</MenuItem>
                <MenuItem value="counselor">Counselor</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={contactFormData.phone}
                onChange={(e) =>
                  handleContactFormChange("phone", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={contactFormData.email}
                onChange={(e) =>
                  handleContactFormChange("email", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={contactFormData.notes}
                onChange={(e) =>
                  handleContactFormChange("notes", e.target.value)
                }
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContactDialog(false)}>Cancel</Button>
          <Button
            onClick={handleContactSubmit}
            variant="contained"
            disabled={!contactFormData.name}
          >
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Communication Dialog */}
      <Dialog
        open={openCommunicationDialog}
        onClose={() => setOpenCommunicationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Log Communication</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Contact"
                value={communicationFormData.contactId}
                onChange={(e) =>
                  handleCommunicationFormChange("contactId", e.target.value)
                }
              >
                {contacts.map((contact) => (
                  <MenuItem
                    key={
                      contact.id ||
                      contact._id ||
                      `contact-${contact.name}-${contact.phone}`
                    }
                    value={contact.id || contact._id || contact.name}
                  >
                    {contact.name} ({contact.relationship})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Communication Type"
                value={communicationFormData.type}
                onChange={(e) =>
                  handleCommunicationFormChange("type", e.target.value)
                }
              >
                <MenuItem value="phone">Phone Call</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="text">Text Message</MenuItem>
                <MenuItem value="in-person">In-Person</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Outcome"
                value={communicationFormData.outcome}
                onChange={(e) =>
                  handleCommunicationFormChange("outcome", e.target.value)
                }
              >
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="left-message">Left Message</MenuItem>
                <MenuItem value="no-answer">No Answer</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={communicationFormData.notes}
                onChange={(e) =>
                  handleCommunicationFormChange("notes", e.target.value)
                }
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Chip
                  label="Follow-up Needed"
                  clickable
                  color={
                    communicationFormData.followUpNeeded ? "primary" : "default"
                  }
                  onClick={() =>
                    handleCommunicationFormChange(
                      "followUpNeeded",
                      !communicationFormData.followUpNeeded
                    )
                  }
                  sx={{ mr: 2 }}
                />
                {communicationFormData.followUpNeeded && (
                  <TextField
                    type="date"
                    label="Follow-up Date"
                    value={communicationFormData.followUpDate || ""}
                    onChange={(e) =>
                      handleCommunicationFormChange(
                        "followUpDate",
                        e.target.value
                      )
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCommunicationDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCommunicationSubmit}
            variant="contained"
            disabled={!communicationFormData.contactId}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ContactInfo;
